import React, { Component, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ error });
  }

  handleRefresh = () => {
    // Force a hard refresh to clear any cached issues
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 3,
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              maxWidth: 400,
              width: '100%',
            }}
          >
            {/* App Icon/Logo */}
            <Box
              sx={{
                width: 80,
                height: 80,
                backgroundColor: '#1976d2',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 2rem',
                color: 'white',
                fontSize: '2rem',
                fontWeight: 'bold',
              }}
            >
              S
            </Box>

            {/* Error Message */}
            <Typography variant="h5" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              The application encountered an error and needs to be refreshed to
              load the latest version.
            </Typography>

            {/* Refresh Button */}
            <Box sx={{ marginTop: 3 }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleRefresh}
                size="large"
                sx={{
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                }}
              >
                Refresh App
              </Button>
            </Box>

            {/* Additional Help */}
            <Box sx={{ marginTop: 3 }}>
              <Typography variant="caption" color="text.secondary">
                If the problem persists, try closing and reopening the app
              </Typography>
            </Box>

            {/* Debug Info (only in development) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box sx={{ marginTop: 2, textAlign: 'left' }}>
                <Typography variant="caption" color="error">
                  Debug Info: {this.state.error.message}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
